import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { FundManagementDetailsDtoIn } from '@/models/in/FundManagementDetailsDtoIn';
import {
  FeaturesForm,
  buildFeaturesForm,
  validateFeaturesForm,
  featuresFormToDto,
  buildUpdateDtoFromFund,
} from './fundViewForm';
import useUpdateFund from './useUpdateFund';
import styles from './FundDialogs.module.css';

interface EditFundFeaturesDialogProps {
  open: boolean;
  onClose: () => void;
  fund: FundManagementDetailsDtoIn;
}

const EditFundFeaturesDialog = ({ open, onClose, fund }: EditFundFeaturesDialogProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FeaturesForm>(() => buildFeaturesForm(fund.features));
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { save, isSaving, serverError, clearServerError } = useUpdateFund(String(fund.id), onClose);

  useEffect(() => {
    if (open) {
      setForm(buildFeaturesForm(fund.features));
      setNote('');
      setValidationError(null);
      clearServerError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fund]);

  const anyEnabled = form.overdraft.enabled || form.fundFreeze.enabled || form.supportRequestsFreeze.enabled;

  const handleSave = () => {
    const error = validateFeaturesForm(form);
    if (error) {
      setValidationError(error);
      return;
    }
    if (!note.trim()) {
      setValidationError('fundsManagement.noteDialog.noteRequired');
      return;
    }
    setValidationError(null);
    save(buildUpdateDtoFromFund(fund, { features: featuresFormToDto(form) }, note.trim()));
  };

  const dateFields = (
    value: { startDate: string; endDate: string },
    onChange: (changes: { startDate?: string; endDate?: string }) => void,
  ) => (
    <>
      <div className={styles.formField}>
        <label className={styles.fieldLabel}>{t('fundsManagement.startDate')}</label>
        <Input type="date" dir="ltr" value={value.startDate} onChange={(e) => onChange({ startDate: e.target.value })} />
      </div>
      <div className={styles.formField}>
        <label className={styles.fieldLabel}>{t('fundsManagement.endDate')}</label>
        <Input type="date" dir="ltr" value={value.endDate} onChange={(e) => onChange({ endDate: e.target.value })} />
      </div>
    </>
  );

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t('fundsManagement.editFeaturesTitle')}
      contentClassName={styles.wideDialogContent}
    >
      <div className={styles.dialogForm}>
        {/* Overdraft */}
        <div className={styles.featureToggleRow}>
          <Checkbox
            checked={form.overdraft.enabled}
            onCheckedChange={(checked) => setForm({ ...form, overdraft: { ...form.overdraft, enabled: checked } })}
          />
          <span>{t('fundsManagement.overdraft')}</span>
        </div>
        {form.overdraft.enabled && (
          <div className={styles.featureFieldsIndent}>
            <div className={styles.formField}>
              <label className={styles.fieldLabel}>{t('fundsManagement.overdraftAmountLabel')}</label>
              <Input
                type="number"
                dir="ltr"
                value={form.overdraft.amount}
                onChange={(e) => setForm({ ...form, overdraft: { ...form.overdraft, amount: e.target.value } })}
              />
            </div>
            {dateFields(form.overdraft, (changes) => setForm({ ...form, overdraft: { ...form.overdraft, ...changes } }))}
            <div className={styles.featureToggleRow}>
              <Checkbox
                checked={form.overdraft.isPositive}
                onCheckedChange={(checked) => setForm({ ...form, overdraft: { ...form.overdraft, isPositive: checked } })}
              />
              <span className={styles.fieldLabel}>{t('fundsManagement.positiveOverdraft')}</span>
            </div>
          </div>
        )}

        {/* Fund freeze */}
        <div className={styles.featureToggleRow}>
          <Checkbox
            checked={form.fundFreeze.enabled}
            onCheckedChange={(checked) => setForm({ ...form, fundFreeze: { ...form.fundFreeze, enabled: checked } })}
          />
          <span>{t('fundsManagement.fundFreeze')}</span>
        </div>
        {form.fundFreeze.enabled && (
          <div className={styles.featureFieldsIndent}>
            {dateFields(form.fundFreeze, (changes) => setForm({ ...form, fundFreeze: { ...form.fundFreeze, ...changes } }))}
          </div>
        )}

        {/* Support requests freeze */}
        <div className={styles.featureToggleRow}>
          <Checkbox
            checked={form.supportRequestsFreeze.enabled}
            onCheckedChange={(checked) => setForm({ ...form, supportRequestsFreeze: { ...form.supportRequestsFreeze, enabled: checked } })}
          />
          <span>{t('fundsManagement.supportRequestsFreeze')}</span>
        </div>
        {form.supportRequestsFreeze.enabled && (
          <div className={styles.featureFieldsIndent}>
            {dateFields(form.supportRequestsFreeze, (changes) => setForm({ ...form, supportRequestsFreeze: { ...form.supportRequestsFreeze, ...changes } }))}
          </div>
        )}

        {/* Reason */}
        {anyEnabled && (
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('fundsManagement.featuresReason')}</label>
            <textarea
              className={styles.dialogTextarea}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </div>
        )}

        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.noteDialog.noteLabel')}</label>
          <textarea
            className={styles.dialogTextarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('fundsManagement.noteDialog.notePlaceholder')}
          />
        </div>

        {validationError && <p className={styles.formError}>{t(validationError)}</p>}
        {serverError && <p className={styles.formError}>{serverError}</p>}

        <div className={styles.formActions}>
          <Button variant="white" onClick={onClose} disabled={isSaving}>
            {t('fundsManagement.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('loading') : t('fundsManagement.save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditFundFeaturesDialog;
